import { Box, Typography } from '@mui/material';
import React, { useContext } from 'react';
import {
  TypedUseSelectorHook,
  useSelector as useReduxSelector,
} from 'react-redux';

import { RootState } from '../../../app/rootReducer';
import { FlexRow } from '../../../common/components/v2';
import { ApplicationDetailedLifecycle } from '../../../common/constants/detailedLifecyle';
import {
  DisputeReason,
  DisputeReasonDisplayMap,
} from '../../../common/constants/disputeReasons';
import { DocumentType } from '../../../common/constants/documentType';
import { Resource } from '../../../common/constants/resource';
import { getCarrierRoles } from '../../../common/constants/roles';
import { TaskState } from '../../../common/constants/states';
import { getFormattedTimestamp } from '../../../common/helpers';
import { AuthContext } from '../../login/AuthWrapper';
import { ITask } from '../../tasks/interfaces/ITask';
import styles from '../styles.module.scss';
import { FileDetail } from './components/FileDetail';
import { FileUpload } from './components/FileUpload';
import { NoraTaskIcon } from './components/NoraTaskIcon';
import { TaskDetailsItem } from './components/TaskDetailsItem';
import { TaskNote } from './components/TaskNote';

interface DisputeTaskData {
  reason: DisputeReason[];
  note: string | null;
}

export const DisputeTaskDetail = ({
  task,
  uploadHandler,
  downloadHandler,
  deleteHandler,
  taskStateHandler,
}: {
  task: ITask;
  uploadHandler: (
    existingTaskData: ITask,
    data: FormData,
    type: string,
  ) => void;
  downloadHandler: (uri: string, fileName: string) => void;
  deleteHandler: (
    existingTaskData: ITask,
    documentId: number,
    reason: string,
  ) => void;
  taskStateHandler: (taskId: number, state: TaskState, reason?: string) => void;
}) => {
  const useSelector: TypedUseSelectorHook<RootState> = useReduxSelector;

  const auth = useContext(AuthContext);
  const isCarrier = Boolean(getCarrierRoles(auth.roles));

  const applicationDetailedLifecycle = useSelector(
    state => state.application.applicationDetailedLifecycle,
  );
  const isCompletedInvestigation =
    applicationDetailedLifecycle ===
    ApplicationDetailedLifecycle.COMPLETED_INVESTIGATION;

  const taskData = task.taskData as unknown as DisputeTaskData;
  const attachments = task.attachments ?? [];
  const reasons: DisputeReason[] = taskData?.reason ?? [];
  const isCompleted = task.state === TaskState.COMPLETED;

  const onFileSelection = (files: any, evidenceType: string) => {
    uploadHandler(task, files, evidenceType);
  };

  const onTaskStateChange = (state: TaskState, reason?: string) => {
    if (state) {
      taskStateHandler(task.id, state, reason);
    }
  };

  return (
    <Box>
      <Box
        display="flex"
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
        ml={-1}
        id={task.id.toString()}
      >
        <Box display="flex" flexDirection="row" alignItems="center">
          <Box mt={0.5}>
            <NoraTaskIcon
              task={task}
              onTaskStateClick={onTaskStateChange}
              disabled={isCarrier}
            />
          </Box>
          <Typography variant="h2">{Resource.TASK_TITLE_DISPUTE}</Typography>
        </Box>
        <Typography color="textSecondary" variant="subtitle2">
          {getFormattedTimestamp(task.retriedAt ?? task.createdAt)}
        </Typography>
      </Box>

      {/* Uploaded files */}
      {attachments.length > 0 && (
        <FileDetail
          documents={attachments}
          fileOpenHandler={downloadHandler}
          fileDeletehandler={
            isCompleted
              ? undefined
              : (documentId: number, reason: string) =>
                  deleteHandler(task, documentId, reason)
          }
        />
      )}

      {/* File upload — hidden once task is completed or case is resolved */}
      {!isCompleted && !isCompletedInvestigation && (
        <FileUpload
          leftItem={attachments.length > 0 ? '' : Resource.FILES}
          selectedFiles={onFileSelection}
          type={DocumentType.DISPUTE}
          ongoingUpload={task.ongoingUpload?.[DocumentType.DISPUTE]}
          uploadType="application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        />
      )}

      {/* Dispute resolution note — read-only once task is completed or case is resolved */}
      <TaskNote
        taskID={task.id}
        originalNote={taskData?.note ?? undefined}
        label="Dispute Resolution Note"
        readOnly={isCompleted || isCompletedInvestigation}
      />

      {reasons.length > 0 && (
        <>
          {/* Dispute reasons — always read-only */}
          <FlexRow className={styles.TaskSubtitle}>{Resource.REASON}</FlexRow>
          <TaskDetailsItem
            leftItem={Resource.EVIDENCE}
            rightItem={reasons.map(r => DisputeReasonDisplayMap[r] ?? r).join(', ')}
          />
        </>
      )}
    </Box>
  );
};
